/**
 * 부착(Attach) 이미지 캐시 (신규 요청 — 참조용 PDF 배치)
 *
 * 렌더러(renderer.ts)는 매 프레임 동기로 그린다 — 이미지 디코딩은 비동기라
 * 렌더 루프 안에서 기다릴 수 없다. 그래서 여기서 src 별로 한 번만 로드해
 * 캐싱하고, 로드가 끝나면 리스너(Canvas.tsx 의 rAF 루프)에게
 * "다시 그려라" 라고만 알린다.
 *
 * ★ UnderlayRef.src 는 Supabase Storage **경로**(고정값, 문서에 영구 저장됨)이고
 *   실제로 그리려면 만료되는 signed URL 이 필요하다. 이 파일(cad/render/*)은
 *   data/* 계층(Supabase 클라이언트)을 몰라야 하므로, 경로 → URL 변환은
 *   `setUnderlaySrcResolver()` 로 주입받는다 — 페이지 부팅 코드(data 계층을
 *   아는 곳)가 한 번 등록한다. **캐시 키는 항상 원래 경로**를 쓴다 —
 *   signed URL 을 키로 쓰면 재발급마다 캐시가 계속 깨지고, 문서에 그 URL을
 *   실수로 저장해 버리는 사고(만료되면 영구히 깨짐)도 막을 수 있다.
 */

const cache = new Map<string, HTMLImageElement>();
const pending = new Set<string>();
const listeners = new Set<() => void>();

type SrcResolver = (path: string) => Promise<string>;
let resolver: SrcResolver | null = null;

/** 저장 경로 → 실제로 fetch 할 수 있는 URL 로 바꾸는 함수를 등록한다 (data 계층이 부팅 시 1회 호출). */
export function setUnderlaySrcResolver(fn: SrcResolver): void {
  resolver = fn;
}

/** 이미지 로드가 끝날 때마다(성공만) 호출된다. 반환값은 구독 해제 함수. */
export function onUnderlayLoaded(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notify(): void {
  for (const cb of listeners) cb();
}

function isDirectlyLoadable(src: string): boolean {
  return (
    src.startsWith('data:') ||
    src.startsWith('http:') ||
    src.startsWith('https:') ||
    src.startsWith('blob:')
  );
}

function beginLoad(cacheKey: string, url: string): void {
  const img = new Image();
  // signed URL(Supabase Storage)도 그리기만 하면 되지만, 나중에 PDF/PNG
  // 내보내기가 캔버스 픽셀을 읽을 때 CORS 로 캔버스가 오염되지 않도록 미리 요청한다.
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    cache.set(cacheKey, img);
    pending.delete(cacheKey);
    notify();
  };
  img.onerror = () => {
    pending.delete(cacheKey);
  };
  img.src = url;
}

/** 캐시에 있으면 즉시 반환. 없으면 로드를 시작하고 null — 로드가 끝나면 리스너가 다시 그리게 한다. */
export function getUnderlayImage(src: string): HTMLImageElement | null {
  const cached = cache.get(src);
  if (cached) return cached;
  if (pending.has(src)) return null;

  pending.add(src);
  if (isDirectlyLoadable(src)) {
    beginLoad(src, src);
  } else if (resolver) {
    resolver(src)
      .then((url) => beginLoad(src, url))
      .catch(() => pending.delete(src));
  } else {
    pending.delete(src); // 리졸버가 아직 등록 안 됐다 — 다음 프레임에 다시 시도
  }
  return null;
}

export function clearUnderlayCache(): void {
  cache.clear();
  pending.clear();
}
